import React, { lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatedSection } from '../components/animated/AnimatedSection';
import { MagneticButton } from '../components/animated/MagneticButton';
import { FeatureCard } from '../components/animated/FeatureCard';
import { LogoSlider } from '../components/animated/LogoSlider';
import { courses } from '../data/courses';
import { BookOpen, Award, Users, Sparkles, TrendingUp, Lightbulb, Clock, MapPin } from 'lucide-react';
export function AcademyPage() {
  const navigate = useNavigate();
  const learningPathways = [{
    icon: <Award className="w-7 h-7 text-blue-600" data-id="element-704" />,
    title: 'Executive Courses',
    description: 'Advanced programs for senior leaders and executives looking to enhance strategic thinking and leadership capabilities.'
  }, {
    icon: <Sparkles className="w-7 h-7 text-green-600" data-id="element-705" />,
    title: 'Creative Industry Courses',
    description: 'Specialized training for creative professionals in media, design, and digital content creation.'
  }, {
    icon: <BookOpen className="w-7 h-7 text-blue-600" data-id="element-706" />,
    title: 'Professional Certifications',
    description: 'Industry-recognized certifications that validate your expertise and boost career prospects.'
  }, {
    icon: <Users className="w-7 h-7 text-green-600" data-id="element-707" />,
    title: 'Soft-Skills Training',
    description: 'Essential interpersonal and communication skills for professional success in any field.'
  }, {
    icon: <TrendingUp className="w-7 h-7 text-blue-600" data-id="element-708" />,
    title: 'Youth Power Skills',
    description: 'Foundational skills for young professionals entering the workforce or starting businesses.'
  }, {
    icon: <Lightbulb className="w-7 h-7 text-green-600" data-id="element-709" />,
    title: 'Digital Skills & Technology Programmes',
    description: 'Cutting-edge tech skills including digital literacy, data analysis, and emerging technologies.'
  }];
  const skillsBuilderLogos = [{
    url: 'https://cdn.magicpatterns.com/uploads/ctisWMyYziyw6VchVdoZwd/Listening_Drop_Shadow_SVG.svg',
    alt: 'Listening Skill'
  }, {
    url: 'https://cdn.magicpatterns.com/uploads/uajggDYkErqKMmAufoGyXT/Speaking_Drop_Shadow_SVG.svg',
    alt: 'Speaking Skill'
  }, {
    url: 'https://cdn.magicpatterns.com/uploads/6bQgrdUFrdsp4gey5zUW75/Problem_Solving_Drop_Shadow_SVG.svg',
    alt: 'Problem Solving Skill'
  }, {
    url: 'https://cdn.magicpatterns.com/uploads/iw5gALSu8Wr5PeEsFYP8g1/Creativity_Drop_Shadow_SVG.svg',
    alt: 'Creativity Skill'
  }, {
    url: 'https://cdn.magicpatterns.com/uploads/d6pD9xaAf5dz4abjXH5WrN/Adapting_Drop_Shadow_SVG.svg',
    alt: 'Adapting Skill'
  }, {
    url: 'https://cdn.magicpatterns.com/uploads/atDREC8qwPsRaa9hFKH7QJ/Planning_Drop_Shadow_SVG.svg',
    alt: 'Planning Skill'
  }, {
    url: 'https://cdn.magicpatterns.com/uploads/2uZCjBBWbYvRJbQDH9G5Ja/Leadership_Drop_Shadow_SVG.svg',
    alt: 'Leadership Skill'
  }, {
    url: 'https://cdn.magicpatterns.com/uploads/ggDu1v4zyJ2BCfxqmRxdu1/Teamwork_Drop_Shadow_SVG.svg',
    alt: 'Teamwork Skill'
  }];
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-710">
      <main className="flex-grow pt-20" data-id="element-711">
        {/* Hero Section */}
        <section className="relative py-32 overflow-hidden" data-id="element-712">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-green-50" data-id="element-713" />
          <div className="absolute top-20 right-10 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20" data-id="element-714" />

          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-715">
            <div className="max-w-4xl mx-auto text-center" data-id="element-716">
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-gray-900 mb-8" data-id="element-717">
                SkillzHQ{' '}
                <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent" data-id="element-718">
                  Academy
                </span>
              </h1>
              <p className="text-2xl text-gray-600 leading-relaxed mb-6" data-id="element-719">
                Building Africa's talent pipeline
              </p>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto" data-id="element-720">
                SkillzHQ Academy offers flexible, practical, and affordable
                learning experiences designed to equip learners with the skills
                needed for today's economy.
              </p>
            </div>
          </div>
        </section>

        {/* Learning Pathways */}
        <AnimatedSection className="py-24 bg-white" data-id="element-721">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-722">
            <div className="max-w-6xl mx-auto" data-id="element-723">
              <div className="text-center mb-16" data-id="element-724">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-725">
                  Our Learning Pathways
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-726">
                  Every course is designed by experts, delivered through a
                  modern, user-friendly platform, and built to ensure real-world
                  application.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-727">
                {learningPathways.map((pathway, index) => <FeatureCard key={index} icon={pathway.icon} title={pathway.title} description={pathway.description} data-id="element-728" />)}
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Skills Builder Partnership Section */}
        <AnimatedSection className="py-16 bg-gradient-to-b from-white to-blue-50" data-id="element-729">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-730">
            <div className="max-w-6xl mx-auto text-center mb-8" data-id="element-731">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-732">
                Powered by Skills Builder Partnership
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed mb-6" data-id="element-733">
                The{' '}
                <a href="https://www.skillsbuilder.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline font-medium transition-colors duration-200" data-id="element-734">
                  Skills Builder Partnership
                </a>{' '}
                includes over 900 partners - working collectively towards the
                mission that one day, everyone will build the essential skills
                to succeed.
              </p>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="element-735">
                Our work is underpinned by the{' '}
                <a href="https://www.skillsbuilder.org/universal-framework" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline font-medium transition-colors duration-200" data-id="element-736">
                  Skills Builder Universal Framework
                </a>
                , which shows how to build essential skills at every stage of
                life. These eight skills are: Listening, Speaking, Problem
                Solving, Creativity, Adapting, Planning, Leadership and
                Teamwork.
              </p>
            </div>
            <LogoSlider logos={skillsBuilderLogos} data-id="element-737" />
          </div>
        </AnimatedSection>

        {/* Featured Courses Section */}
        <AnimatedSection className="py-24 bg-gradient-to-b from-blue-50 to-white" data-id="element-738">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-739">
            <div className="max-w-6xl mx-auto" data-id="element-740">
              <div className="text-center mb-16" data-id="element-741">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-742">
                  Featured Courses
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-743">
                  Our courses empower learners to grow regardless of age or
                  background.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-744">
                {courses.map(course => <div key={course.id} className="group bg-white rounded-2xl overflow-hidden border-2 border-gray-100 hover:border-blue-300 hover:shadow-2xl transition-all duration-500 cursor-pointer hover:-translate-y-2" onClick={() => navigate(`/courses/${course.id}`)} data-id="element-745">
                    <div className="relative h-48 overflow-hidden" data-id="element-746">
                      <img src={course.image} alt={course.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" data-id="element-747" />
                    </div>

                    <div className="p-6" data-id="element-748">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300" data-id="element-749">
                        {course.title}
                      </h3>
                      <p className="text-gray-600 mb-4 leading-relaxed line-clamp-2" data-id="element-750">
                        {course.description}
                      </p>

                      <div className="flex flex-wrap gap-4 mb-4" data-id="element-751">
                        <div className="flex items-center gap-2 text-gray-600" data-id="element-752">
                          <Clock className="w-4 h-4 text-blue-600" data-id="element-753" />
                          <span className="text-sm" data-id="element-754">{course.duration}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600" data-id="element-755">
                          <MapPin className="w-4 h-4 text-green-600" data-id="element-756" />
                          <span className="text-sm" data-id="element-757">Virtual</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-gray-200" data-id="element-758">
                        <div data-id="element-759">
                          <div className="text-2xl font-bold text-gray-900" data-id="element-760">
                            {course.price}
                          </div>
                          <div className="text-sm text-gray-600" data-id="element-761">
                            {course.priceUSD}
                          </div>
                        </div>
                        <MagneticButton variant="primary" size="sm" data-id="element-762">
                          Learn More
                        </MagneticButton>
                      </div>
                    </div>
                  </div>)}
              </div>

              <div className="text-center" data-id="element-763">
                <p className="text-2xl font-semibold text-gray-900 mb-8" data-id="element-764">
                  Learn at your pace. Grow at your potential.
                </p>
                <MagneticButton variant="primary" size="lg" onClick={() => navigate('/courses')} data-id="element-765">
                  Explore All Courses
                </MagneticButton>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* CTA Section */}
        <AnimatedSection className="py-32 bg-gradient-to-br from-blue-600 to-green-600 relative overflow-hidden" data-id="element-766">
          <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]" data-id="element-767" />
          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-768">
            <div className="max-w-4xl mx-auto text-center" data-id="element-769">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-8" data-id="element-770">
                Ready to Start Learning?
              </h2>
              <p className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto" data-id="element-771">
                Join thousands of learners transforming their careers with
                SkillzHQ Academy
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-772">
                <MagneticButton variant="secondary" size="lg" onClick={() => navigate('/courses')} data-id="element-773">
                  Browse Courses
                </MagneticButton>
                <MagneticButton variant="outline" size="lg" onClick={() => navigate('/contact')} data-id="element-774">
                  Get in Touch
                </MagneticButton>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </main>
    </div>;
}